import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCommunityUserStats } from '../../hooks/useCommunityUserStats';
import { LuArrowUpRight, LuBuilding2, LuQuote, LuStar } from 'react-icons/lu';
import MarketingShell from '../marketing/MarketingShell';
import MarketingLoginLink from '../marketing/MarketingLoginLink';
import PageMeta from '../seo/PageMeta';
import {
  ACTIVE_CLIENT_SEGMENTS,
  FEEDBACK_SUMMARY,
  PRODUCT_OUTCOMES,
  PRODUCT_STATS,
  TESTIMONIALS,
  USER_FEEDBACK,
} from '../../config/productHubContent';
import { organizationJsonLd, softwareApplicationJsonLd, webSiteJsonLd } from '../../config/seo';
import { QNAREAD } from '../../config/appLabels';
import ProductPlanFeaturesSection from '../marketing/ProductPlanFeaturesSection';

const highlights = [
  { title: 'Search & learn', text: 'Server-side search, filters, and a home dashboard built for daily study.', to: '/features' },
  { title: 'Practice & AI', text: 'Spaced repetition, saved decks, and metered AI explain on Pro.', to: '/features' },
  { title: 'Teams & collaboration', text: 'Organizations, invites, assignments, challenges, and team leaderboards for cohorts and companies.', to: '/teams' },
  { title: 'Plans & checkout', text: 'Free to start; Pro via Stripe; Team workspaces after sign-in.', to: '/pricing' },
  { title: 'Security first', text: 'HttpOnly sessions, optional MFA, and admin-only sensitive APIs.', to: '/security' },
];

function StarRow({ count = 5, size = 16 }) {
  return (
    <span className="product-stars" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <LuStar key={i} style={{ width: size, height: size }} />
      ))}
    </span>
  );
}

function RatingStars({ rating }) {
  const full = Math.floor(rating);
  return (
    <span className="product-stars product-stars--rated" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <LuStar
          key={i}
          className={i < full ? 'is-filled' : 'is-muted'}
          aria-hidden
        />
      ))}
    </span>
  );
}

function communityStatCard(stats, loading, status) {
  if (loading) {
    return {
      key: 'community',
      value: '…',
      label: 'Learners joined',
      detail: 'Loading live community stats',
      live: true,
    };
  }
  if (stats) {
    const total = Number(stats.count ?? 0);
    const active = Number(stats.active ?? 0);
    return {
      key: 'community',
      value: total.toLocaleString(),
      label: 'Learners joined',
      detail:
        active > 0
          ? `${active.toLocaleString()} active recently on the platform`
          : 'Registered accounts learning on QnaRead',
      live: true,
    };
  }
  return {
    key: 'community',
    value: status === 'error' ? '—' : '0',
    label: 'Learners joined',
    detail:
      status === 'error'
        ? 'Connect the API to show live user counts'
        : 'Be among the first learners on QnaRead',
    live: status !== 'error',
  };
}

const ProductHubPage = () => {
  const { stats, loading, status } = useCommunityUserStats();

  const platformStats = useMemo(
    () => [communityStatCard(stats, loading, status), ...PRODUCT_STATS],
    [stats, loading, status],
  );

  return (
  <MarketingShell variant="product" layout="hub">
    <PageMeta
      jsonLd={[webSiteJsonLd(), organizationJsonLd(), softwareApplicationJsonLd()]}
    />
    <div className="product-hero-band product-hero-band--mega">
      <span className="product-eyebrow">Product · QnaRead</span>
      <h1 className="product-hub-title">Learn any subject with structured Q&amp;A, not chaos</h1>
      <p className="product-hub-tagline">{QNAREAD.tagline}</p>
      <p className="product-lead product-lead--wide">
        Search notebooks across your library — courses, interview prep, certifications, or team playbooks — practice with
        spaced repetition, review on a dashboard, and upgrade when you need more AI, all from one workspace after sign-in.
      </p>
      <div className="marketing-hero-actions product-hub-hero-actions">
        <MarketingLoginLink className="marketing-btn marketing-btn-primary product-cta" redirect="/dashboard">
          Get started — sign in
        </MarketingLoginLink>
        <Link to="/features" className="marketing-btn marketing-btn-secondary product-cta-secondary">
          View product tour
        </Link>
      </div>
      <ul className="product-hub-pills" aria-label="Highlights">
        {stats && Number(stats.count) > 0 ? (
          <li className="product-hub-pill--accent">
            {Number(stats.count).toLocaleString()} learners joined
          </li>
        ) : null}
        <li>Free tier</li>
        <li>Google sign-in</li>
        <li>Optional MFA</li>
        <li>Stripe Pro checkout</li>
        <li>Team workspaces</li>
      </ul>
    </div>

    <ProductPlanFeaturesSection />

    <section className="product-stats-band" aria-labelledby="product-stats-heading">
      <h2 id="product-stats-heading" className="product-section-eyebrow">
        Platform at a glance
      </h2>
      <ul className="product-stats-grid">
        {platformStats.map((stat) => (
          <li
            key={stat.key ?? stat.label}
            className={`product-stat-card${stat.live ? ' product-stat-card--live' : ''}`}
          >
            {stat.live ? <span className="product-stat-live-badge">Live</span> : null}
            <span className="product-stat-value">{stat.value}</span>
            <span className="product-stat-label">{stat.label}</span>
            <p className="product-stat-detail">{stat.detail}</p>
          </li>
        ))}
      </ul>
      <ul className="product-outcome-row">
        {PRODUCT_OUTCOMES.map((row) => (
          <li key={row.metric}>
            <strong>{row.metric}</strong>
            <span>{row.label}</span>
          </li>
        ))}
      </ul>
    </section>

    <section className="product-clients-band" aria-labelledby="product-clients-heading">
      <div className="product-clients-head">
        <LuBuilding2 className="product-section-icon" aria-hidden />
        <div>
          <h2 id="product-clients-heading">Active learners &amp; teams</h2>
          <p>Studying and shipping knowledge with QnaRead across roles and industries.</p>
        </div>
      </div>
      <ul className="product-clients-list">
        {ACTIVE_CLIENT_SEGMENTS.map((segment) => (
          <li key={segment}>{segment}</li>
        ))}
      </ul>
    </section>

    <section className="product-feedback-band" aria-labelledby="product-feedback-heading">
      <div className="product-feedback-summary">
        <RatingStars rating={FEEDBACK_SUMMARY.average} />
        <p className="product-feedback-score">
          <strong>{FEEDBACK_SUMMARY.average}</strong>
          <span>/ 5</span>
        </p>
        <p className="product-feedback-count">{FEEDBACK_SUMMARY.countLabel}</p>
        <p className="product-feedback-note">{FEEDBACK_SUMMARY.note}</p>
      </div>
      <div className="product-feedback-cards">
        <h2 id="product-feedback-heading" className="product-section-eyebrow product-section-eyebrow--left">
          User feedback
        </h2>
        <ul className="product-feedback-list">
          {USER_FEEDBACK.map((item) => (
            <li key={item.context} className="product-feedback-card">
              <StarRow count={item.rating} size={14} />
              <p className="product-feedback-text">&ldquo;{item.text}&rdquo;</p>
              <span className="product-feedback-context">{item.context}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="product-testimonials" aria-labelledby="product-testimonials-heading">
      <h2 id="product-testimonials-heading" className="product-section-title">
        What teams are saying
      </h2>
      <p className="product-section-lead">
        Representative stories from managers, L&amp;D leads, and learners using QnaRead in production workflows.
      </p>
      <ul className="product-testimonial-grid">
        {TESTIMONIALS.map((t) => (
          <li key={t.name} className="product-testimonial-card">
            <LuQuote className="product-testimonial-quote-icon" aria-hidden />
            <blockquote className="product-testimonial-quote">{t.quote}</blockquote>
            <footer className="product-testimonial-foot">
              <span className="product-testimonial-avatar" aria-hidden>
                {t.initials}
              </span>
              <div>
                <cite className="product-testimonial-name">{t.name}</cite>
                <p className="product-testimonial-role">
                  {t.role}
                  <span className="product-testimonial-org"> · {t.org}</span>
                </p>
              </div>
            </footer>
          </li>
        ))}
      </ul>
    </section>

    <div className="marketing-grid product-hub-grid">
      {highlights.map((item) => (
        <Link key={item.title} to={item.to} className="product-feature-block product-hub-card">
          <h3>{item.title}</h3>
          <p>{item.text}</p>
          <span className="product-hub-link">Learn more →</span>
        </Link>
      ))}
    </div>

    <section className="product-hub-cta-band">
      <h2>Ready to try it?</h2>
      <p>Create a free account or sign in with Google — then complete a short onboarding tour.</p>
      <div className="marketing-hero-actions">
        <MarketingLoginLink className="marketing-btn marketing-btn-primary" redirect="/dashboard">
          Sign in
        </MarketingLoginLink>
        <Link to="/contact" className="marketing-btn marketing-btn-secondary">
          Talk to us
        </Link>
        <Link to="/pricing" className="product-pricing-link">
          Compare plans
          <LuArrowUpRight aria-hidden />
        </Link>
      </div>
    </section>
  </MarketingShell>
  );
};

export default ProductHubPage;
